import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { trackToolError } from '@/shared/lib/telemetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  toolId?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for |Loading (?:CSS )?chunk [\w-]+ failed/i;
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

function isChunkLoadError(error?: Error): boolean {
  if (!error) return false;
  return error.name === 'ChunkLoadError' || CHUNK_LOAD_ERROR_PATTERN.test(error.message);
}

function shouldAutomaticallyReloadChunk(toolId?: string): boolean {
  try {
    const key = `easyutils:chunk-reload:${window.location.pathname}:${toolId || 'unknown'}`;
    const previousReload = Number(window.sessionStorage.getItem(key));
    const now = Date.now();
    if (
      Number.isFinite(previousReload) &&
      previousReload > 0 &&
      now - previousReload < CHUNK_RELOAD_COOLDOWN_MS
    ) {
      return false;
    }
    window.sessionStorage.setItem(key, String(now));
    return true;
  } catch {
    // Keep the manual recovery available when browser storage is disabled.
    return false;
  }
}

function ErrorFallback({
  error,
  onRetry,
  toolId,
}: {
  error?: Error;
  onRetry: () => void;
  toolId?: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center space-y-6 max-w-md mx-auto p-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-destructive/10 p-3">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold">Tool Loading Failed</h2>
          <p className="text-muted-foreground">
            {toolId ? `The "${toolId}" tool hit a technical error. ` : ''}
            Try again, or return home if the problem continues.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border bg-muted/50 p-3 text-left">
            <p className="font-mono text-sm text-muted-foreground">Error: {error.message}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>

          <Button onClick={() => navigate('/')} variant="outline" className="gap-2">
            <Home className="h-4 w-4" />
            Go Home
          </Button>
        </div>

        <div className="text-xs text-muted-foreground">
          If this problem persists, please contact support.
        </div>
      </div>
    </div>
  );
}

export class ToolErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { toolId } = this.props;
    trackToolError(toolId || 'unknown', error, {
      componentStack: errorInfo.componentStack,
    });
    if (isChunkLoadError(error) && shouldAutomaticallyReloadChunk(toolId)) {
      window.location.reload();
    }
  }

  handleRetry = () => {
    const { error } = this.state;
    if (isChunkLoadError(error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback, children, toolId } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return <ErrorFallback error={error} onRetry={this.handleRetry} toolId={toolId} />;
    }

    return children;
  }
}

// HOC for wrapping tools with error boundary
export function withToolErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  toolId?: string,
): React.ComponentType<P> {
  return function WithBoundary(props: P) {
    return (
      <ToolErrorBoundary toolId={toolId}>
        <WrappedComponent {...props} />
      </ToolErrorBoundary>
    );
  };
}
